import ArrowDownIcon from "@/assets/icons/arrow-down.svg";
import { firstPurchasePromoStore } from "@/store/firstPurchasePromo";
import { userStore } from "@/store/user";
import { Resolution } from "@/types/common";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { VidMuseEvent } from "../../common/events";
import { logger } from "../../common/logger";
import { UpgradeTip } from "./UpgradeTip";
import Resolution1080pIcon from "./assets/resolution-1080p.svg";
import Resolution720pIcon from "./assets/resolution-720p.svg";

export interface ResolutionSelectorProps {
  value: Resolution;
  onChange: (value: Resolution) => void;
  disabled?: boolean;
  supportedResolutions?: Resolution[];
}

export const ResolutionSelector = observer(function ResolutionSelector({
  value,
  onChange,
  disabled = false,
  supportedResolutions,
}: ResolutionSelectorProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    bottom: 0,
  });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isOptionDisabled = (resolution: Resolution) => {
    if (disabled) return true;
    if (!supportedResolutions) {
      return false;
    }
    return !supportedResolutions.includes(resolution);
  };

  // Auto-switch from 1080p to 720p for free users
  useEffect(() => {
    if (userStore.isFreeUser && value === Resolution.FULL_HD) {
      onChange(Resolution.HD);
    }
  }, [value, onChange]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  // Handle outside click and close on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", () => setIsOpen(false), true);
      window.addEventListener("resize", () => setIsOpen(false));
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", () => setIsOpen(false), true);
      window.removeEventListener("resize", () => setIsOpen(false));
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 200; // Width of dropdown
      const buttonCenter = rect.left + rect.width / 2;
      const centeredLeft = buttonCenter - dropdownWidth / 2;

      setDropdownPos({
        left: centeredLeft,
        top: 0,
        bottom: window.innerHeight - rect.top + 8, // 8px gap above button
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (resolution: Resolution) => {
    if (isOptionDisabled(resolution)) {
      return;
    }

    // Block 1080p for free users
    if (resolution === Resolution.FULL_HD && userStore.isFreeUser) {
      logger.event(VidMuseEvent.FreeUserClickResolutionHD);
      // 显示首单优惠弹窗，点击 Claim 后跳转到 Pricing 页面
      firstPurchasePromoStore.tryShow(() => {
        router.push("/pricing");
      });
      setIsOpen(false);
      return;
    }

    onChange(resolution);
    setIsOpen(false);
  };

  const getIcon = (resolution: Resolution) => {
    return resolution === Resolution.HD ? (
      <Resolution720pIcon className="w-5 h-5" />
    ) : (
      <Resolution1080pIcon className="w-5 h-5" />
    );
  };

  const getCheckmark = (resolution: Resolution) => {
    if (value !== resolution) return null;

    return (
      <svg className="w-3 h-3 text-cyan-400" viewBox="0 0 12 12" fill="none">
        <path
          d="M10 3L4.5 8.5L2 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const getLockIcon = () => {
    return (
      <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
        <path d="M6 1a2.5 2.5 0 0 0-2.5 2.5V5h5V3.5A2.5 2.5 0 0 0 6 1Z" />
        <path d="M2 5.5A1.5 1.5 0 0 0 .5 7v3A1.5 1.5 0 0 0 2 11.5h8A1.5 1.5 0 0 0 11.5 10V7A1.5 1.5 0 0 0 10 5.5H2Z" />
      </svg>
    );
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={handleToggle}
        disabled={disabled}
        data-testid="resolution-selector-trigger"
        className={classNames(
          "flex items-center h-10 rounded-full transition-all duration-200",
          "bg-[rgba(33,33,41,0.70)] border border-[rgba(255,255,255,0.05)]",
          "hover:border-[rgba(255,255,255,0.05)] focus:outline-none",
          "px-2 md:px-3", // Icon-only on mobile/tablet, full width on desktop
          "justify-center md:justify-between gap-0 md:gap-1", // Center icon on mobile/tablet, space-between on desktop
          {
            "border-[rgba(255,255,255,0.05)]": isOpen,
            "opacity-50 cursor-not-allowed": disabled,
          },
        )}
      >
        <span className="flex items-center gap-[6px] text-sm font-normal text-white">
          {getIcon(value)}
          <span className="hidden md:inline">{value}</span>
        </span>
        <ArrowDownIcon
          className={classNames(
            "w-4 h-4 text-text-primary transition-transform duration-200 hidden md:block",
            { "rotate-180": isOpen },
          )}
        />
      </button>

      {/* Dropdown Menu (Portal) */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPos.left,
              bottom: dropdownPos.bottom,
              zIndex: 9999,
            }}
            data-testid="resolution-selector-dropdown"
            className="w-[200px] bg-[#121212] border border-white/10 rounded-xl shadow-2xl backdrop-blur-xl overflow-hidden ring-1 ring-black/50"
          >
            <div className="p-1">
              {/* 720p Option */}
              <button
                onClick={() => handleSelect(Resolution.HD)}
                disabled={isOptionDisabled(Resolution.HD)}
                data-testid="resolution-option-720p"
                className={classNames(
                  "w-full text-left p-3 rounded-lg flex items-center justify-between group transition-colors",
                  {
                    "hover:bg-white/5": !isOptionDisabled(Resolution.HD),
                    "cursor-not-allowed opacity-50": isOptionDisabled(
                      Resolution.HD,
                    ),
                  },
                )}
              >
                <div className="flex items-center gap-3">
                  {getIcon(Resolution.HD)}
                  <div className="text-sm font-medium text-white flex items-center gap-2">
                    {Resolution.HD}
                    {getCheckmark(Resolution.HD)}
                  </div>
                </div>
              </button>

              {/* 1080p Option */}
              <button
                onClick={() => handleSelect(Resolution.FULL_HD)}
                disabled={isOptionDisabled(Resolution.FULL_HD)}
                data-testid="resolution-option-1080p"
                className={classNames(
                  "w-full text-left p-3 rounded-lg flex items-center justify-between group transition-colors",
                  {
                    "hover:bg-white/5": !isOptionDisabled(Resolution.FULL_HD),
                    "cursor-not-allowed opacity-50": isOptionDisabled(
                      Resolution.FULL_HD,
                    ),
                  },
                )}
              >
                <div
                  className={classNames("flex items-center gap-3", {
                    "opacity-60": userStore.isFreeUser,
                  })}
                >
                  {getIcon(Resolution.FULL_HD)}
                  <div className="text-sm font-medium text-white flex items-center gap-2">
                    {Resolution.FULL_HD}
                    {getCheckmark(Resolution.FULL_HD)}
                  </div>
                </div>
                {userStore.isFreeUser && <UpgradeTip />}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
});
